import { Entity } from '@core/entities/entity'
import { plainToInstance } from 'class-transformer'
import { IProject } from '@core/entities/project/Project/IProject'
import { ProjectStatus } from '@core/entities/project'
import { ProjectPermission } from '@core/entities/project/Project/types'
import { INavigationProjectIntegration, ProjectRole } from '@core/entities/user'
import { localizedStrings } from '@core/strings'
import { statuses } from '@core/theme/statuses'
import { Feature, FeatureFlagMap } from '@core/entities/featureFlags'

export class Project extends Entity<IProject> {
  id: number

  organisationId: string | number

  name: string

  code: string

  externalCode: string

  description: string

  status?: ProjectStatus

  address: {
    line1: string
    postcode: string
    city: string
  }

  startDate: string

  endDate: string

  permittedActions: ProjectPermission[]

  canInviteRoles: ProjectRole[]

  mapBoxConfigResult: {
    mapBoxStyleUrl?: string
  }

  timezone: string

  isTest: boolean

  logoUrl?: string

  authGroups: Array<{
    id: number
    name: string
  }>

  businessUnits?: Array<{
    id: number
    name: string
    code: string
  }>

  projectSidebarInfo?: {
    hasTasks: boolean
    hasPermits: boolean
    hasChecklists: boolean
    hasInspections: boolean
    hasRegisters: boolean
    hasPlans: boolean
    hasMapConfigured: boolean
    hasObservations?: boolean
    integrations?: INavigationProjectIntegration[]
  }

  checkInEnabled?: boolean

  counters?: {
    assignedActiveTasksCount?: number
  }

  featureFlags?: FeatureFlagMap | null

  public static new(payload: unknown): Project {
    if (
      payload &&
      typeof payload === 'object' &&
      'projectNavbarInfo' in payload &&
      !('projectSidebarInfo' in payload)
    ) {
      const p = payload as Record<string, unknown>
      p.projectSidebarInfo = p.projectNavbarInfo
    }
    return plainToInstance(Project, payload)
  }

  public hasPermission(permission: ProjectPermission): boolean {
    return this.permittedActions?.includes(permission)
  }

  public canSeeAuthorisationGroups(): boolean {
    return this.hasPermission('LIST_GROUPS')
  }

  public canEdit(): boolean {
    return this.hasPermission('EDIT')
  }

  public canEditGroups(): boolean {
    return this.hasPermission('CREATE_GROUPS')
  }

  public canEditSiteLocations(): boolean {
    return this.hasPermission('EDIT_LOCATIONS')
  }

  public canAddPermitTemplateToProjects(): boolean {
    return this.hasPermission('ASSIGN_TEMPLATES')
  }

  public canAddChecklistTemplateToProjects(): boolean {
    return this.hasPermission('ASSIGN_CHECKLIST_TEMPLATES')
  }

  public canAddTaskTemplateToProjects(): boolean {
    return this.hasPermission('ASSIGN_TASK_TEMPLATES')
  }

  public canAssignPermissionsToTemplate(): boolean {
    return this.hasPermission('ASSIGN_PERMISSIONS')
  }

  public canAssignPermissionsToPermitTemplate(): boolean {
    return (
      this.canEdit() || this.hasPermission('ASSIGN_PERMISSIONS_PERMIT_TEMPLATE')
    )
  }

  public canChangeUserStatus(): boolean {
    return this.hasPermission('CHANGE_MEMBER_STATUS')
  }

  public canViewTimeline(): boolean {
    return this.hasPermission('LIST_AUDITS')
  }

  public canUpdateUserAuthGroups(): boolean {
    return this.hasPermission('UPDATE_USER_AUTH_GROUPS')
  }

  public canChangeUserRole(): boolean {
    return this.hasPermission('CHANGE_MEMBER_ROLE')
  }

  public canSeeAllPermits(): boolean {
    return this.hasPermission('SEE_ALL_PERMITS')
  }

  public canDeleteProjectUser(): boolean {
    return this.hasPermission('CHANGE_MEMBER_STATUS')
  }

  public canManageProjectTemplate(): boolean {
    return this.canEdit() || this.hasPermission('EDIT_PROJECT_TEMPLATE')
  }

  public canManageProjectPermitTemplate(): boolean {
    return (
      this.canManageProjectTemplate() ||
      this.hasPermission('EDIT_PROJECT_PERMIT_TEMPLATE')
    )
  }
  public canAssignProjectDocumentTemplates(): boolean {
    return this.hasPermission('ASSIGN_DOCUMENT_ENGINE_TEMPLATES')
  }

  public canRemoveProjectDocumentTemplates(): boolean {
    return this.hasPermission('REMOVE_DOCUMENT_ENGINE_TEMPLATES')
  }

  public canAssignApprovals(): boolean {
    return this.canEdit() || this.hasPermission('ASSIGN_APPROVALS')
  }

  public canUpdateProject(): boolean {
    return this.canEdit() || this.hasPermission('UPDATE_PROJECT')
  }

  public canCreateSiteBoxDocuments(): boolean {
    return this.hasPermission('CREATE_SITEBOX_DOCUMENTS')
  }

  public canEditSiteBoxFolder(): boolean {
    return this.hasPermission('EDIT_FOLDER_SITE_BOX')
  }

  public canExportUsers(): boolean {
    return this.hasPermission('LIST_MEMBERS')
  }

  public canInviteUsers(): boolean {
    return this.hasPermission('INVITE_USER')
  }

  public canViewCheckedInUsers(): boolean {
    return !!this.checkInEnabled && this.hasPermission('DASHBOARD')
  }

  public canCreateDigests(): boolean {
    return this.hasPermission('CREATE_DIGEST')
  }

  public canDeleteDigests(): boolean {
    return this.hasPermission('DELETE_DIGEST')
  }

  public canListDigests(): boolean {
    return this.hasPermission('LIST_DIGEST')
  }

  public canReadDigests(): boolean {
    return this.hasPermission('READ_DIGEST')
  }

  public isFeatureEnabled(feature: Feature): boolean {
    return !!this.featureFlags?.[feature]
  }

  public canAccessDigests(): boolean {
    return (
      this.canCreateDigests() ||
      this.canDeleteDigests() ||
      this.canListDigests() ||
      this.canReadDigests()
    )
  }

  public static statusText(status: ProjectStatus) {
    switch (status) {
      case 'LIVE':
        return statuses.active.label
      case 'ARCHIVED':
        return statuses.archived.label
      case 'TEST':
        return localizedStrings.test
      default:
        return status
    }
  }

  public static statusColour(status: ProjectStatus) {
    switch (status) {
      case 'LIVE':
        return statuses.active.color
      default:
        return 'default'
    }
  }
}
